import '../../components/common/PhotoUpload/PhotoUpload.css';

import React, { useEffect, useRef, useState } from 'react';

import CameraUploadButton from '../../components/common/CameraUploadButton/CameraUploadButton';
import ConfirmDialog from '../../components/common/ConfirmDialog/ConfirmDialog';
import FullscreenPhotoViewer
  from '../../components/common/FullscreenPhotoViewer/FullscreenPhotoViewer';
import IconButton from '../../components/common/IconButton/IconButton';
import TrashIcon from '../../components/common/TrashIcon';
import type { AthletePhotoPayload } from '../../services/athletePhotoService';
import { compressImage, compressImageThumbnail } from '../../utils/imageUtils';

const EMPTY_PAYLOAD: AthletePhotoPayload = {
  imageUrl: '',
  imageThumbnailUrl: '',
  storagePathFull: '',
  storagePathThumb: '',
};

export interface DemoPhotoUploadProps {
  disabled?: boolean;
  label?: string;
  className?: string;
  imageUrl?: string;
  onImageUpload: (payload: AthletePhotoPayload) => void;
  statusText?: string;
  compactEmpty?: boolean;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to encode image'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Demo photo upload — base64 data URLs. Survives reloads via localStorage.
 *
 * Why data URLs and not blob URLs (createObjectURL):
 *   blob: URLs are tied to the page lifetime and become invalid after reload.
 *   The user-added athlete persists in localStorage, so the URL we save needs
 *   to survive too. compressImage shrinks to ~400px @ q=0.6 → ~30–80 KB; well
 *   under the ~5 MB localStorage cap for the demo's worst case.
 */
const DemoPhotoUpload: React.FC<DemoPhotoUploadProps> = ({
  disabled = false,
  label = 'Athlete photo',
  className = '',
  imageUrl,
  onImageUpload,
  statusText,
  compactEmpty = false,
}) => {
  const [imagePreview, setImagePreview] = useState<string | null>(imageUrl ?? null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setImagePreview(imageUrl?.trim() ? imageUrl : null);
  }, [imageUrl]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const [fullBlob, thumbBlob] = await Promise.all([
        compressImage(file),
        compressImageThumbnail(file),
      ]);
      const [fullDataUrl, thumbDataUrl] = await Promise.all([
        blobToDataUrl(fullBlob),
        blobToDataUrl(thumbBlob),
      ]);
      setImagePreview(fullDataUrl);
      onImageUpload({
        imageUrl: fullDataUrl,
        imageThumbnailUrl: thumbDataUrl,
        storagePathFull: '',
        storagePathThumb: '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process image.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = (): void => {
    setImagePreview(null);
    onImageUpload(EMPTY_PAYLOAD);
  };

  const rootClass = ['photo-upload', className, compactEmpty ? 'photo-upload--compact-empty' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass}>
      {label ? <div className="photo-upload-label">{label}</div> : null}

      {!imagePreview ? (
        <div className="photo-upload-container">
          <CameraUploadButton
            className="photo-upload-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || uploading}
            ariaLabel={label}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="photo-upload-hidden-input"
            onChange={(e) => { void handleFile(e); }}
            disabled={disabled || uploading}
            aria-label={label}
          />
        </div>
      ) : (
        <div className="photo-upload-preview">
          <button
            type="button"
            className="photo-upload-preview-open"
            onClick={() => setViewerOpen(true)}
            aria-label={`Open ${label} full screen`}
          >
            <span className="photo-upload-preview-frame">
              <img src={imagePreview} alt="" className="photo-upload-preview-image" />
            </span>
          </button>
          <div className="photo-upload-side-actions">
            {uploading ? (
              <div
                className="photo-upload-remove photo-upload-spinner-shell"
                aria-label="Processing photo"
                role="status"
              >
                <span className="photo-upload-spinner" aria-hidden="true" />
              </div>
            ) : (
              <IconButton
                className="photo-upload-remove"
                aria-label="Remove photo"
                title="Remove photo"
                onClick={() => setConfirmRemoveOpen(true)}
                disabled={disabled}
              >
                <TrashIcon />
              </IconButton>
            )}
            {statusText ? (
              <span className="photo-upload-side-status">{statusText}</span>
            ) : null}
          </div>
          <FullscreenPhotoViewer
            open={viewerOpen}
            src={imagePreview}
            onClose={() => setViewerOpen(false)}
            viewerLabel={`${label} — preview`}
          />
          <ConfirmDialog
            open={confirmRemoveOpen}
            title="Remove photo?"
            message="Are you sure you want to remove this photo?"
            confirmLabel="Remove"
            onCancel={() => setConfirmRemoveOpen(false)}
            onConfirm={() => {
              setConfirmRemoveOpen(false);
              handleRemove();
            }}
          />
        </div>
      )}
      {error ? (
        <div className="photo-upload-error" role="alert">{error}</div>
      ) : null}
    </div>
  );
};

export default DemoPhotoUpload;
